import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  MapPin, Loader2, AlertCircle, Sunrise, Sun, Sunset, Moon, Star,
  Search, Settings as SettingsIcon, ChevronDown, ChevronRight,
  RefreshCw, Compass, Bell, BellOff, Vibrate, Volume2, Play, Pause,
  StarOff, X as XIcon, Plane,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  prayerTimesService, PRAYER_KEYS, PRAYER_LABEL_FR, ALERT_MODES,
  PRAYER_METHODS, SCHOOLS, computeNextPrayer, computeActiveAdhan, formatCountdown,
  defaultAlertsAllOff, buildPrayerObjects,
} from '../services/prayerTimesService';
import { geolocationService, GeolocationError } from '../services/geolocationService';
import { notificationService } from '../services/notificationService';
import AdhanFullscreen from '../components/prayer/AdhanFullscreen';
import { toast } from 'sonner';

const PRAYER_ICONS = {
  Fajr:    Star,
  Sunrise: Sunrise,
  Dhuhr:   Sun,
  Asr:     Sun,
  Maghrib: Sunset,
  Isha:    Moon,
};

const PrayerTimes = () => {
  const [prefs, setPrefs] = useState(() => prayerTimesService.getLocalPrefs());
  const [timings, setTimings] = useState(null);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permState, setPermState] = useState('unknown');
  const [now, setNow] = useState(() => new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [adhanSounds, setAdhanSounds] = useState([]);
  const [previewSoundId, setPreviewSoundId] = useState(null);
  const previewAudioRef = useRef(null);
  const [notifPerm, setNotifPerm] = useState('default');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  /* Tick every second for the countdown */
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* Permission probe + remote prefs sync + adhan catalog */
  useEffect(() => {
    // Initialize notifications on page load
    notificationService.initialize().catch((e) => {
      console.warn('Notification init failed on PrayerTimes:', e);
    });
    
    geolocationService.getPermissionState().then(setPermState);
    prayerTimesService.loadRemotePrefs().then((p) => {
      // Backfill alerts with defaults if missing
      if (!p.alerts || Object.keys(p.alerts).length === 0) {
        p = { ...p, alerts: defaultAlertsAllOff() };
      }
      if (!Array.isArray(p.favorite_locations)) p.favorite_locations = [];
      setPrefs(p);
    });
    prayerTimesService.getAdhanSounds().then(setAdhanSounds);
    notificationService.getPermissionState().then((s) => setNotifPerm(s));
  }, []);

  /* Travel detection — silent geoloc probe (only if already granted) */
  const [travelSuggestion, setTravelSuggestion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const state = await geolocationService.getPermissionState();
      if (state !== 'granted') return;
      const cur = prefs.last_location;
      if (!cur?.lat) return;
      try {
        const { lat, lng } = await geolocationService.requestCurrent({ timeout: 8000 });
        if (cancelled) return;
        // Haversine distance in km
        const R = 6371;
        const dLat = (lat - cur.lat) * Math.PI / 180;
        const dLng = (lng - cur.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2
          + Math.cos(cur.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180)
          * Math.sin(dLng/2)**2;
        const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        if (d > 50) {
          // We're far from last_location → reverse geocode and surface a toast.
          try {
            const rev = await prayerTimesService.reverseGeocode(lat, lng);
            const label = [rev.city, rev.country].filter(Boolean).join(', ');
            if (label && label !== cur.label) {
              setTravelSuggestion({ lat, lng, label, city: rev.city, country: rev.country, distKm: d });
            }
          } catch { /* ignore */ }
        }
      } catch { /* silent — user must have re-denied */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptTravelUpdate = () => {
    if (!travelSuggestion) return;
    const { lat, lng, label, city, country } = travelSuggestion;
    updatePref({ last_location: { lat, lng, label, city, country } });
    toast.success(`Localisation mise à jour : ${label}`);
    setTravelSuggestion(null);
  };

  /* Schedule notifications whenever timings or alerts change */
  useEffect(() => {
    if (!timings || !prefs.alerts) return;
    const prayers = buildPrayerObjects(timings, new Date());
    notificationService.scheduleAll({
      prayers,
      alerts: prefs.alerts,
      soundCatalog: adhanSounds,
      locationLabel: prefs.last_location?.label,
    }).catch(() => { /* non-blocking */ });
  }, [timings, prefs.alerts, adhanSounds, prefs.last_location?.label]);

  /* Fetch timings whenever (prefs.last_location, method, school) changes */
  const fetchTimings = useCallback(async () => {
    const loc = prefs.last_location;
    if (!loc?.lat || !loc?.lng) return;
    setLoading(true); setError(null);
    try {
      const r = await prayerTimesService.getTimings({
        lat: loc.lat, lng: loc.lng,
        method: prefs.method ?? 12,
        school: prefs.school ?? 0,
      });
      setTimings(r.timings);
      setDate(r.date);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [prefs.last_location, prefs.method, prefs.school]);

  useEffect(() => { fetchTimings(); }, [fetchTimings]);

  /* Geolocate handler */
  const handleAutoLocate = async () => {
    setError(null); 
    setLoading(true);
    try {
      const result = await geolocationService.requestCurrent({ timeout: 12000 });
      const { lat, lng } = result;
      
      let label = 'Position actuelle';
      try {
        const rev = await prayerTimesService.reverseGeocode(lat, lng);
        label = [rev.city, rev.country].filter(Boolean).join(', ') || label;
      } catch { /* offline reverse-geocode is non-blocking */ }
      
      const newPrefs = { ...prefs, last_location: { lat, lng, label } };
      setPrefs(newPrefs);
      prayerTimesService.saveLocalPrefs(newPrefs);
      prayerTimesService.pushRemotePrefs(newPrefs);
      const after = await geolocationService.getPermissionState();
      setPermState(after);
      
      toast.success(`Localisé: ${label}`);
    } catch (e) {
      if (e instanceof GeolocationError) {
        if (e.kind === 'denied') {
          setError("Vous avez refusé la géolocalisation. Saisissez une adresse ci-dessous, ou réactivez la permission dans les réglages de votre appareil.");
          setShowManual(true);
        } else if (e.kind === 'timeout') {
          setError("La géolocalisation a expiré. Réessayez ou saisissez votre adresse manuellement.");
        } else {
          setError("Géolocalisation indisponible sur cet appareil. Saisissez votre adresse.");
          setShowManual(true);
        }
      } else {
        setError(e?.message || 'Erreur inattendue');
      }
      setPermState(await geolocationService.getPermissionState());
    } finally {
      setLoading(false);
    }
  };

  /* Manual search (debounced) */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search || search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await prayerTimesService.geocode(search);
        setSearchResults(results);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
  }, [search]);

  const pickResult = (r) => {
    const label = [r.city, r.country].filter(Boolean).join(', ') || r.display_name;
    const newPrefs = { ...prefs, last_location: {
      lat: r.lat, lng: r.lng, label, city: r.city, country: r.country,
    } };
    setPrefs(newPrefs);
    prayerTimesService.saveLocalPrefs(newPrefs);
    prayerTimesService.pushRemotePrefs(newPrefs);
    setSearch(''); setSearchResults([]); setShowManual(false);
    toast.success(`Localisation : ${label}`);
  };

  const updatePref = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    prayerTimesService.saveLocalPrefs(next);
    prayerTimesService.pushRemotePrefs(next);
  };

  const updateAlert = (prayerKey, patch) => {
    const nextAlerts = {
      ...(prefs.alerts || defaultAlertsAllOff()),
      [prayerKey]: { ...(prefs.alerts?.[prayerKey] || { mode: 'off', sound_id: 'mecca', iqama_delay_min: 0 }), ...patch },
    };
    updatePref({ alerts: nextAlerts });
  };

  /* Favorites */
  const isCurrentFavorite = useMemo(() => {
    const cur = prefs.last_location;
    if (!cur) return false;
    return (prefs.favorite_locations || []).some(
      (f) => Math.abs(f.lat - cur.lat) < 0.001 && Math.abs(f.lng - cur.lng) < 0.001,
    );
  }, [prefs.last_location, prefs.favorite_locations]);

  const toggleFavorite = () => {
    const cur = prefs.last_location;
    if (!cur) return;
    let nextFavs;
    if (isCurrentFavorite) {
      nextFavs = (prefs.favorite_locations || []).filter(
        (f) => !(Math.abs(f.lat - cur.lat) < 0.001 && Math.abs(f.lng - cur.lng) < 0.001),
      );
      toast.success('Favori retiré');
    } else {
      if ((prefs.favorite_locations || []).length >= 20) {
        toast.error('Maximum 20 favoris');
        return;
      }
      nextFavs = [...(prefs.favorite_locations || []), { ...cur }];
      toast.success(`Favori ajouté : ${cur.label || 'localisation'}`);
    }
    updatePref({ favorite_locations: nextFavs });
  };

  const pickFavorite = (fav) => {
    updatePref({ last_location: { ...fav } });
    toast.success(`Localisation : ${fav.label || 'favori'}`);
  };

  const removeFavorite = (idx, e) => {
    e?.stopPropagation();
    const nextFavs = (prefs.favorite_locations || []).filter((_, i) => i !== idx);
    updatePref({ favorite_locations: nextFavs });
  };

  const handleEnableNotifications = async () => {
    try {
      await notificationService.initialize();
      const granted = await notificationService.requestPermission();
      const state = await notificationService.getPermissionState();
      setNotifPerm(state);
      
      if (granted) {
        toast.success('Notifications activées');
      } else {
        toast.error('Notifications refusées. Activez-les dans les réglages.');
      }
    } catch (e) {
      toast.error('Erreur lors de l\'activation des notifications');
    }
  };

  const handlePreviewSound = (soundId) => {
    if (typeof Audio === 'undefined') return;
    const sound = adhanSounds.find((s) => s.id === soundId);
    if (!sound?.url) return;
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (previewSoundId === soundId) {
      setPreviewSoundId(null);
      return;
    }
    const a = new Audio(sound.url);
    a.play().then(() => {
      previewAudioRef.current = a;
      setPreviewSoundId(soundId);
      a.addEventListener('ended', () => setPreviewSoundId(null), { once: true });
    }).catch(() => {
      toast.error("Impossible de lire l'aperçu (autoplay bloqué ?)");
      setPreviewSoundId(null);
    });
  };

  useEffect(() => () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
  }, []);

  const next = useMemo(() => computeNextPrayer(timings, now), [timings, now]);
  const activeAdhan = useMemo(() => computeActiveAdhan(timings, now), [timings, now]);
  const hijri = date?.hijri;

  /* ────────────────────────────────────────────────────────────────────
   *  Fullscreen Adhan overlay
   *  - Auto-opens when activeAdhan transitions from null → set, unless
   *    the user has disabled `adhan_fullscreen_auto` in their prefs.
   *  - Can also be opened manually via the "Adhan en cours" badge.
   *  - Closes once the audio timeline finishes (handled inside the
   *    overlay), or via the close button / Esc.
   * ──────────────────────────────────────────────────────────────────── */
  const [adhanOverlay, setAdhanOverlay] = useState(null);
  // `null` when closed, `{ key, at, audioUrl }` when open.
  const lastAutoOpenedKeyRef = useRef(null);
  const adhanFullscreenAuto = prefs.adhan_fullscreen_auto !== false;

  useEffect(() => {
    if (!activeAdhan) return;
    if (!adhanFullscreenAuto) return;
    // Auto-open only once per (prayer, day) so closing the overlay doesn't
    // immediately re-open it on the next tick.
    const tag = `${activeAdhan.key}-${activeAdhan.at.toDateString()}`;
    if (lastAutoOpenedKeyRef.current === tag) return;
    lastAutoOpenedKeyRef.current = tag;
    const cfg = prefs.alerts?.[activeAdhan.key];
    const soundId = cfg?.sound_id || 'mecca';
    const sound = adhanSounds.find((s) => s.id === soundId);
    setAdhanOverlay({
      key: activeAdhan.key,
      at: activeAdhan.at,
      audioUrl: sound?.url || null,
      soundId,
    });
  }, [activeAdhan, adhanFullscreenAuto, prefs.alerts, adhanSounds]);

  const openAdhanOverlayManually = (key) => {
    const cfg = prefs.alerts?.[key];
    const soundId = cfg?.sound_id || 'mecca';
    const sound = adhanSounds.find((s) => s.id === soundId);
    setAdhanOverlay({ key, at: new Date(), audioUrl: sound?.url || null, soundId });
  };

  const closeAdhanOverlay = () => setAdhanOverlay(null);

  /* Demo trigger — visiting /prieres/horaires?adhan_demo=1 (or =Fajr) opens
   * the fullscreen Adhan overlay without waiting for a real prayer time.
   * Useful for QA / for users who just want to preview the experience. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const demo = params.get('adhan_demo');
    if (!demo) return;
    const key = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'].includes(demo)
      ? demo : 'Dhuhr';
    const cfg = prefs.alerts?.[key];
    const soundId = cfg?.sound_id || 'mecca';
    const sound = adhanSounds.find((s) => s.id === soundId);
    setAdhanOverlay({ key, at: new Date(), audioUrl: sound?.url || null, soundId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adhanSounds]);

  /* Inbound invite (deep-link from someone else's "share an adhan" flow).
   * Surfaces a one-shot toast inviting the visitor to join the prayer. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (!invite) return;
    const label = PRAYER_LABEL_FR[invite] || 'la prière';
    toast(`Vous êtes invité à rejoindre ${label}.`, {
      description: 'Un proche partage ce moment avec vous.',
      duration: 6000,
    });
  }, []);

  return (
    <div className="min-h-screen py-8 px-4" data-testid="prayer-times-page">
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          overline="Prières"
          title="Horaires de prière"
          subtitle="Adaptés à votre localisation, avec méthode de calcul et madhab personnalisables."
        />

        {/* Travel detection banner */}
        {travelSuggestion && (
          <div
            className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm"
            data-testid="prayer-travel-banner"
          >
            <Plane className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-text-primary">
                Vous semblez être à <strong>{travelSuggestion.label}</strong>
                <span className="text-text-muted"> (~{travelSuggestion.distKm.toFixed(0)} km de {prefs.last_location?.label || 'votre dernière position'}).</span>
              </div>
              <div className="text-xs text-text-muted mt-0.5">
                Voulez-vous mettre à jour vos horaires pour cette ville ?
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                onClick={acceptTravelUpdate}
                className="bg-primary text-background"
                data-testid="prayer-travel-accept-btn"
              >
                Mettre à jour
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTravelSuggestion(null)}
                data-testid="prayer-travel-dismiss-btn"
              >
                Plus tard
              </Button>
            </div>
          </div>
        )}

        {/* Location card */}
        <GlassCard className="mt-6" data-testid="prayer-location-card">
          {loading && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Récupération de votre position en cours... (jusqu'à 12 secondes)</span>
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-100 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-text-muted uppercase tracking-wider">Localisation</div>
                <div className="text-base font-serif text-text-primary truncate" data-testid="prayer-location-label">
                  {prefs.last_location?.label || 'Aucune localisation'}
                </div>
                {prefs.last_location && (
                  <div className="text-xs text-text-muted font-mono">
                    {prefs.last_location.lat.toFixed(4)}, {prefs.last_location.lng.toFixed(4)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Desktop: full button with text */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoLocate}
                disabled={loading}
                data-testid="prayer-auto-locate-btn"
                className={`${loading ? 'bg-blue-50 dark:bg-blue-950' : ''} hidden sm:inline-flex`}
                title="Me localiser"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Compass className="w-4 h-4 mr-2" />
                    Me localiser
                  </>
                )}
              </Button>

              {/* Mobile: icon-only button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoLocate}
                disabled={loading}
                data-testid="prayer-auto-locate-btn-mobile"
                className={`${loading ? 'bg-blue-50 dark:bg-blue-950' : ''} inline-flex sm:hidden`}
                title="Me localiser"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Compass className="w-4 h-4" />
                )}
              </Button>

              {/* Desktop: full button with text */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManual((v) => !v)}
                data-testid="prayer-manual-toggle-btn"
                className="hidden sm:inline-flex"
                title="Rechercher une adresse"
              >
                <Search className="w-4 h-4 mr-1" />Adresse
              </Button>

              {/* Mobile: icon-only button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManual((v) => !v)}
                data-testid="prayer-manual-toggle-btn-mobile"
                className="inline-flex sm:hidden"
                title="Rechercher une adresse"
              >
                <Search className="w-4 h-4" />
              </Button>

              {prefs.last_location && (
                <>
                  {/* Desktop: full button with text */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFavorite}
                    data-testid="prayer-fav-toggle-btn"
                    title={isCurrentFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    className="hidden sm:inline-flex"
                  >
                    {isCurrentFavorite
                      ? <><StarOff className="w-4 h-4 mr-1" />Retirer</>
                      : <><Star className="w-4 h-4 mr-1" />Favori</>}
                  </Button>

                  {/* Mobile: icon-only button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFavorite}
                    data-testid="prayer-fav-toggle-btn-mobile"
                    title={isCurrentFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    className="inline-flex sm:hidden"
                  >
                    {isCurrentFavorite
                      ? <StarOff className="w-4 h-4" />
                      : <Star className="w-4 h-4" />}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Favorites strip */}
          {(prefs.favorite_locations || []).length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="prayer-favs-strip">
              <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Favoris :</span>
              {prefs.favorite_locations.map((fav, i) => {
                const isActive = prefs.last_location
                  && Math.abs(fav.lat - prefs.last_location.lat) < 0.001
                  && Math.abs(fav.lng - prefs.last_location.lng) < 0.001;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickFavorite(fav)}
                    className={`group inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs
                                border transition-colors ${
                                  isActive
                                    ? 'border-primary/40 bg-primary/15 text-primary'
                                    : 'border-border bg-surface-elevated hover:bg-surface-elevated/70 text-text-primary'
                                }`}
                    data-testid={`prayer-fav-${i}`}
                  >
                    <MapPin className="w-3 h-3" />
                    {fav.city || fav.label || `${fav.lat.toFixed(2)},${fav.lng.toFixed(2)}`}
                    <span
                      onClick={(e) => removeFavorite(i, e)}
                      className="ml-0.5 text-text-muted hover:text-rose-400"
                      title="Supprimer"
                      data-testid={`prayer-fav-${i}-remove`}
                    >
                      <XIcon className="w-3 h-3" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {showManual && (
            <div className="mt-4 space-y-2" data-testid="prayer-manual-block">
              <Input
                placeholder="Rechercher une ville (ex. Marseille, Tunis…)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-surface border-border"
                data-testid="prayer-search-input"
                autoFocus
              />
              {searching && (
                <div className="flex items-center gap-2 text-xs text-text-muted">
                  <Loader2 className="w-3 h-3 animate-spin" />Recherche…
                </div>
              )}
              {!searching && searchResults.length > 0 && (
                <div className="border border-border rounded-md overflow-hidden">
                  {searchResults.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => pickResult(r)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface-elevated transition-colors
                                 border-b border-border last:border-b-0"
                      data-testid={`prayer-search-result-${i}`}
                    >
                      <div className="text-text-primary truncate">{r.display_name}</div>
                      <div className="text-xs text-text-muted">{r.city || '—'} · {r.country || '—'}</div>
                    </button>
                  ))}
                </div>
              )}
              {!searching && search.length >= 2 && searchResults.length === 0 && (
                <div className="text-xs text-text-muted">Aucun résultat — vérifiez l'orthographe.</div>
              )}
            </div>
          )}

          {permState === 'denied' && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200/90">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
              <span>
                La géolocalisation est refusée. Pour l'activer, ouvrez les réglages de votre navigateur ou application,
                puis autorisez l'accès à la position pour ce site/app.
              </span>
            </div>
          )}
        </GlassCard>

        {error && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm" data-testid="prayer-error">
            <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
            <span className="text-rose-200/90">{error}</span>
          </div>
        )}

        {/* Next prayer + countdown */}
        {next && timings && (
          <GlassCard className="mt-4 border-primary/30 bg-primary/5" data-testid="prayer-next-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-text-muted uppercase tracking-wider">Prochaine prière</div>
                <div className="font-serif text-3xl text-primary mt-1">{next.label}</div>
                <div className="text-sm text-text-secondary mt-0.5">
                  à {next.at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-text-muted uppercase tracking-wider">Dans</div>
                <div className="font-mono text-3xl text-text-primary tabular-nums" data-testid="prayer-countdown">
                  {formatCountdown(next.msUntil)}
                </div>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Today's timings */}
        {timings ? (
          <GlassCard className="mt-4" data-testid="prayer-timings-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-serif text-lg text-text-primary">
                {date?.readable || 'Aujourd\'hui'}
              </h3>
              <div className="flex items-center gap-2">
                {hijri && (
                  <span className="text-xs text-text-muted">
                    {hijri.day} {hijri.month?.en} {hijri.year} H.
                  </span>
                )}
                <Button variant="ghost" size="icon" onClick={fetchTimings} disabled={loading} data-testid="prayer-refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            <div className="divide-y divide-border" data-testid="prayer-list">
              {PRAYER_KEYS.map((k) => {
                const Icon = PRAYER_ICONS[k] || Sun;
                const time = (timings[k] || '').split(' ')[0];
                const isNext = next?.key === k;
                const isActive = activeAdhan?.key === k;
                const remainingSec = isActive
                  ? Math.max(0, Math.ceil(activeAdhan.msRemaining / 1000))
                  : 0;
                return (
                  <div
                    key={k}
                    className={`relative flex items-center justify-between py-3 px-3 -mx-1 rounded-md transition-all ${
                      isActive
                        ? 'bg-primary/10 text-primary ring-1 ring-primary/40 prayer-adhan-glow'
                        : isNext
                          ? 'text-primary'
                          : 'text-text-primary'
                    }`}
                    data-testid={`prayer-row-${k}`}
                    data-active-adhan={isActive ? 'true' : 'false'}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${isActive ? 'text-primary' : isNext ? 'text-primary' : 'text-text-muted'}`} />
                      <span className="font-serif text-base">{PRAYER_LABEL_FR[k]}</span>
                      {isActive && (
                        <button
                          onClick={() => openAdhanOverlayManually(k)}
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded
                                     bg-primary text-background font-semibold animate-pulse
                                     hover:bg-primary-hover transition-colors"
                          data-testid={`prayer-active-badge-${k}`}
                          aria-label="Ouvrir le mode immersif de l'Adhan"
                        >
                          Adhan en cours
                        </button>
                      )}
                      {!isActive && isNext && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-primary/20 text-primary">à venir</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {isActive && (
                        <span className="font-mono text-[11px] text-primary/80" data-testid={`prayer-adhan-remaining-${k}`}>
                          {Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, '0')}
                        </span>
                      )}
                      <span className="font-mono text-lg tabular-nums">{time || '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        ) : (
          loading ? <PageLoading /> : (
            !prefs.last_location && (
              <GlassCard className="mt-4 text-center py-8" data-testid="prayer-empty-state">
                <Compass className="w-8 h-8 text-text-muted mx-auto mb-2" />
                <p className="text-text-secondary">Activez la localisation ou saisissez une ville pour afficher les horaires.</p>
              </GlassCard>
            )
          )
        )}

        {/* Rappels par prière (mode silent/vibrate/adhan + son + iqama) */}
        <div className="mt-4 border border-border rounded-lg overflow-hidden" data-testid="prayer-alerts">
          <button
            type="button"
            onClick={() => setShowAlerts((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5
                       bg-surface-elevated/40 hover:bg-surface-elevated text-sm text-text-primary"
            data-testid="prayer-alerts-toggle"
          >
            <span className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-text-muted" />Rappels par prière
            </span>
            {showAlerts ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {showAlerts && (
            <div className="p-3 space-y-3 border-t border-border">
              {/* Notification permission banner */}
              {notifPerm !== 'granted' && (
                <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-200/90 mb-1">
                      Pour recevoir des notifications quand l'app est fermée, autorisez les notifications.
                    </p>
                    <Button size="sm" variant="outline" onClick={handleEnableNotifications} data-testid="prayer-enable-notifs">
                      <Bell className="w-3.5 h-3.5 mr-1" />Activer les notifications
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-text-muted">
                Pour chaque prière, choisissez le type de rappel. Pratique pour passer en silencieux ou vibreur pendant le travail,
                et recevoir l'adhan complet à la maison.
              </p>

              {PRAYER_KEYS.map((k) => {
                const cfg = prefs.alerts?.[k] || { mode: 'off', sound_id: 'mecca', iqama_delay_min: 0 };
                const Icon = PRAYER_ICONS[k] || Sun;
                const modeIcon = cfg.mode === 'off' ? BellOff
                  : cfg.mode === 'vibrate' ? Vibrate
                  : cfg.mode === 'adhan' ? Volume2
                  : Bell;
                const ModeIcon = modeIcon;
                return (
                  <div
                    key={k}
                    className="border border-border rounded p-2.5 space-y-2"
                    data-testid={`prayer-alert-${k}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-text-muted" />
                        <span className="font-serif text-base text-text-primary">{PRAYER_LABEL_FR[k]}</span>
                        <ModeIcon className="w-3.5 h-3.5 text-primary" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase text-text-muted">Mode</Label>
                        <Select
                          value={cfg.mode}
                          onValueChange={(v) => updateAlert(k, { mode: v })}
                        >
                          <SelectTrigger className="bg-surface border-border h-9" data-testid={`prayer-alert-${k}-mode`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ALERT_MODES.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {cfg.mode === 'adhan' && (
                        <div>
                          <Label className="text-[10px] uppercase text-text-muted">Adhan</Label>
                          <Select
                            value={cfg.sound_id || (adhanSounds[0]?.id || 'mecca')}
                            onValueChange={(v) => updateAlert(k, { sound_id: v })}
                          >
                            <SelectTrigger className="bg-surface border-border h-9" data-testid={`prayer-alert-${k}-sound`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {adhanSounds.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label className="text-[10px] uppercase text-text-muted">Rappel iqama (min)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={60}
                          value={cfg.iqama_delay_min || 0}
                          onChange={(e) => updateAlert(k, { iqama_delay_min: Math.max(0, Math.min(60, parseInt(e.target.value || '0', 10))) })}
                          className="bg-surface border-border h-9"
                          data-testid={`prayer-alert-${k}-iqama`}
                        />
                      </div>
                    </div>
                    {cfg.mode === 'adhan' && (
                      <button
                        type="button"
                        onClick={() => handlePreviewSound(cfg.sound_id || adhanSounds[0]?.id)}
                        className="text-xs text-primary flex items-center gap-1 hover:underline"
                        data-testid={`prayer-alert-${k}-preview`}
                      >
                        {previewSoundId === cfg.sound_id
                          ? <><Pause className="w-3 h-3" />Stop</>
                          : <><Play className="w-3 h-3" />Écouter l'aperçu</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="mt-4 border border-border rounded-lg overflow-hidden" data-testid="prayer-settings">
          <button
            type="button"
            onClick={() => setShowSettings((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5
                       bg-surface-elevated/40 hover:bg-surface-elevated text-sm text-text-primary"
            data-testid="prayer-settings-toggle"
          >
            <span className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-text-muted" />Paramètres de calcul
            </span>
            {showSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {showSettings && (
            <div className="p-3 space-y-3 border-t border-border">
              <div>
                <Label className="text-xs text-text-secondary">Méthode de calcul</Label>
                <Select
                  value={String(prefs.method ?? 12)}
                  onValueChange={(v) => updatePref({ method: Number(v) })}
                >
                  <SelectTrigger className="bg-surface border-border" data-testid="prayer-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRAYER_METHODS.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-text-secondary">Madhab (Asr)</Label>
                <Select
                  value={String(prefs.school ?? 0)}
                  onValueChange={(v) => updatePref({ school: Number(v) })}
                >
                  <SelectTrigger className="bg-surface border-border" data-testid="prayer-school-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHOOLS.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-text-muted flex items-center gap-1">
                <Bell className="w-3 h-3" />
                Configurez les rappels par prière dans la section "Rappels par prière" ci-dessus.
              </p>

              {/* Adhan immersive fullscreen toggle */}
              <div
                className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg
                           bg-surface-elevated/40 border border-border"
                data-testid="adhan-fullscreen-toggle-row"
              >
                <div className="min-w-0">
                  <div className="text-sm text-text-primary font-serif">Mode Adhan immersif</div>
                  <p className="text-xs text-text-muted leading-snug">
                    Affiche un plein écran spirituel avec le texte de l'Adhan synchronisé
                    à chaque appel à la prière.
                  </p>
                </div>
                <label className="inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={adhanFullscreenAuto}
                    onChange={(e) => updatePref({ adhan_fullscreen_auto: e.target.checked })}
                    data-testid="adhan-fullscreen-toggle"
                  />
                  <div className="w-10 h-5 bg-surface peer-checked:bg-primary border border-border rounded-full
                                  relative transition-colors
                                  after:content-[''] after:absolute after:top-0.5 after:left-0.5
                                  after:w-4 after:h-4 after:rounded-full after:bg-background
                                  peer-checked:after:translate-x-5 after:transition-transform" />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Adhan overlay (manual + auto trigger) */}
      <AdhanFullscreen
        open={!!adhanOverlay}
        prayerKey={adhanOverlay?.key}
        prayerAt={adhanOverlay?.at}
        audioUrl={adhanOverlay?.audioUrl}
        soundId={adhanOverlay?.soundId}
        locationLabel={prefs.last_location?.label}
        onClose={closeAdhanOverlay}
      />
    </div>
  );
};

export default PrayerTimes;
